import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-10% 0px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} data-id="element-107">
      {children}
    </motion.div>;
};
export function NarrativeSection() {
  return <section className="w-full bg-[#050505] text-[#f5f0e8] py-20 md:py-32 px-4 overflow-hidden" data-id="element-108">
      <div className="max-w-2xl mx-auto space-y-24" data-id="element-109">
        {/* PART A: THE PAIN */}
        <div className="space-y-8" data-id="element-110">
          <FadeIn data-id="element-111">
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[#f5f0e8]" data-id="element-112">
              Hay un tipo de dolor en el trading que casi nadie admite en voz
              alta.
            </h2>
          </FadeIn>

          <FadeIn data-id="element-113">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-114">
              No es "perder dinero". Es esto: Terminar una sesión sabiendo que{' '}
              <strong className="text-[#f5f0e8]" data-id="element-115">no fuiste tú</strong> el que
              operó.
            </p>
          </FadeIn>

          <FadeIn data-id="element-116">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-117">
              Que tu plan estaba claro. Que tus reglas estaban ahí. Que incluso{' '}
              <em data-id="element-118">sabías</em> lo que iba a pasar si te saltabas una sola… …y aun
              así lo hiciste.
            </p>
          </FadeIn>

          <FadeIn data-id="element-119">
            <div className="pl-6 border-l-2 border-[#d4af37] my-8 space-y-4 italic text-[#d4af37]/90" data-id="element-120">
              <p data-id="element-121">"Hoy ejecuto. Hoy soy serio. Hoy hago lo correcto."</p>
            </div>
          </FadeIn>

          <FadeIn data-id="element-122">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-123">
              Pasa algo. Y de pronto estás persiguiendo el precio, moviendo el
              stop, aumentando tamaño, tomando trades mediocres, operando por
              rabia o por aburrimiento…
            </p>
            <p className="text-lg text-[#8a8578] leading-relaxed mt-4" data-id="element-124">
              …y al final, cuando se apaga la pantalla, vuelves a prometerte lo
              mismo: "Mañana sí."
            </p>
            <p className="text-lg text-[#f5f0e8] font-medium mt-4" data-id="element-125">
              Pero mañana, el mismo patrón te espera.
            </p>
          </FadeIn>
        </div>

        {/* IMAGE BREAK */}
        <FadeIn data-id="element-126">
          <div className="relative my-16 rounded-lg overflow-hidden border border-[#2a2a2a]" data-id="element-127">
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" data-id="element-128"></div>
            <img src="https://cdn.magicpatterns.com/uploads/f5wRwj18h8X8DT8dGcbe5Q/Imagen_problema_1.png" alt="Trader sufriendo una pérdida" className="w-full h-auto object-cover opacity-80" data-id="element-129" />
          </div>
        </FadeIn>

        {/* PART B: THE PROBLEM */}
        <div className="space-y-8" data-id="element-130">
          <FadeIn data-id="element-131">
            <h3 className="font-serif text-2xl md:text-3xl text-[#d4af37] mb-6" data-id="element-132">
              Lo que casi nadie te dijo: tu cuenta no la opera tu mente
              consciente
            </h3>
          </FadeIn>

          <FadeIn data-id="element-133">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-134">
              Tu mente consciente es la que entiende estrategias. La que mira
              videos. Pero el trading real —el que define tu PnL— no se decide
              ahí. Se decide en la parte de ti que reacciona cuando hay presión.
              Esa parte es tu <strong data-id="element-135">subconsciente</strong>.
            </p>
          </FadeIn>

          <FadeIn data-id="element-136">
            <div className="bg-[#0a0a0a] border border-[#222] p-8 rounded-xl my-8" data-id="element-137">
              <p className="text-[#8a8578] mb-6 text-sm uppercase tracking-wider" data-id="element-138">
                Tu programa interno:
              </p>
              <ul className="space-y-3" data-id="element-139">
                {['Condicionamientos antiguos', 'Necesidad de control', "Necesidad de validar que 'sí puedes'", 'Miedo a quedar como tonto', 'Impulso de reparar dolor rápido', 'Hábito de buscar dopamina', 'Castigo silencioso'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-140">
                    <span className="text-[#d4af37] mt-1.5 h-1.5 w-1.5 rounded-full bg-[#d4af37] flex-shrink-0" data-id="element-141" />
                    <span data-id="element-142">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn data-id="element-143">
            <p className="text-xl md:text-2xl font-serif text-[#f5f0e8] text-center border-y border-[#222] py-8 my-8" data-id="element-144">
              Saber no es lo mismo que obedecer.
            </p>
          </FadeIn>
        </div>

        {/* PART C: IDENTITY */}
        <div className="space-y-8" data-id="element-145">
          <FadeIn data-id="element-146">
            <h3 className="font-serif text-2xl md:text-3xl text-[#f5f0e8]" data-id="element-147">
              El problema real: no estás construyendo identidad. Estás
              persiguiendo resultados.
            </h3>
          </FadeIn>

          <FadeIn data-id="element-148">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-149">
              Si eres trader, seguramente admiras a alguien. No por su
              Lamborghini. Lo admiras porque se nota que esa persona opera con
              sobriedad. Con una calma que no es "zen"… sino profesional.
            </p>
          </FadeIn>

          <FadeIn data-id="element-150">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12" data-id="element-151">
              <div className="space-y-4" data-id="element-152">
                <h4 className="text-[#d4af37] font-medium border-b border-[#d4af37]/30 pb-2 mb-4" data-id="element-153">
                  El Trader Rentable
                </h4>
                <ul className="space-y-3 text-sm text-[#f5f0e8]" data-id="element-154">
                  <li className="flex gap-2" data-id="element-155">
                    <span className="text-green-500" data-id="element-156">✓</span> No negocia reglas
                    con emoción
                  </li>
                  <li className="flex gap-2" data-id="element-157">
                    <span className="text-green-500" data-id="element-158">✓</span> No convierte el
                    trading en terapia
                  </li>
                  <li className="flex gap-2" data-id="element-159">
                    <span className="text-green-500" data-id="element-160">✓</span> No opera para
                    sentirse poderoso
                  </li>
                  <li className="flex gap-2" data-id="element-161">
                    <span className="text-green-500" data-id="element-162">✓</span> No reacciona con
                    rabia
                  </li>
                </ul>
              </div>
              <div className="space-y-4 opacity-60" data-id="element-163">
                <h4 className="text-[#8a8578] font-medium border-b border-[#8a8578]/30 pb-2 mb-4" data-id="element-164">
                  La Identidad Fracturada
                </h4>
                <div className="space-y-4 text-sm" data-id="element-165">
                  <div data-id="element-166">
                    <p className="text-[#f5f0e8] font-medium mb-1" data-id="element-167">En frío:</p>
                    <p className="italic" data-id="element-168">
                      "Soy disciplinado. Soy serio. Sé lo que hago."
                    </p>
                  </div>
                  <div data-id="element-169">
                    <p className="text-[#f5f0e8] font-medium mb-1" data-id="element-170">
                      En caliente:
                    </p>
                    <p className="italic" data-id="element-171">
                      "Solo hoy… solo este trade… solo para recuperar…"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* IMAGE: Fractured identity pain */}
          <FadeIn data-id="element-172">
            <div className="relative rounded-lg overflow-hidden border border-[#2a2a2a]" data-id="element-173">
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" data-id="element-174"></div>
              <img src="https://cdn.magicpatterns.com/uploads/avJSi19yVcTfn8Fk89yn2m/Imagen_del_problema_2.png" alt="Trader enfrentando la identidad fracturada" className="w-full h-auto object-cover opacity-80" data-id="element-175" />
            </div>
          </FadeIn>
        </div>

        {/* PART D: THE MARKET */}
        <div className="space-y-8" data-id="element-176">
          <FadeIn data-id="element-177">
            <h3 className="font-serif text-2xl md:text-3xl text-[#f5f0e8]" data-id="element-178">
              El mercado no te destruye. <br data-id="element-179" />
              <span className="text-[#d4af37]" data-id="element-180">
                El mercado activa tu programación.
              </span>
            </h3>
          </FadeIn>

          <FadeIn data-id="element-181">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-182">
              El mercado no es personal. Pero tú sí lo vuelves personal cuando
              interpretas la información como:
            </p>
          </FadeIn>

          <FadeIn data-id="element-183">
            <div className="flex flex-wrap gap-3 my-6" data-id="element-184">
              {['me quitó algo', 'me está probando', 'me está humillando', 'me debe', 'tengo que demostrar'].map((item, i) => <span key={i} className="px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-full text-[#d4af37] text-sm italic" data-id="element-185">
                  "{item}"
                </span>)}
            </div>
          </FadeIn>

          <FadeIn data-id="element-186">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-187">
              Ese es el momento donde dejas de ser trader… y te conviertes en
              alguien que está buscando regular emociones con clicks. Y eso crea
              venganza, sobreoperación y miedo.
            </p>
          </FadeIn>

          <FadeIn data-id="element-188">
            <div className="p-6 border-l-4 border-[#d4af37] bg-gradient-to-r from-[#d4af37]/10 to-transparent mt-12" data-id="element-189">
              <p className="text-2xl font-serif text-[#f5f0e8]" data-id="element-190">
                No es falta de conocimiento. <br data-id="element-191" />
                Es falta de sistema interno.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}